import React from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IStock } from '@/interfaces/stock';
import { addStock } from '@/adapters/stock';
import ProductSelector from '@/components/selector/product-finder-selector';
import { IProduct } from '@/interfaces/product';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { dateFormat } from '@/configs/dateFormat';

interface IProps {
  dataModal?: IDataModal<Partial<IStock>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const StockAddModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const [form] = Form.useForm<IStock>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IStock>) => await addStock(data),
    mutationKey: ['actionStock'],
    onSuccess: data => {
      if (data.status !== 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'นำเข้าสต็อกสำเร็จ',
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IStock>) => {
        values.coaExpiredDate = dayjs(values.coaExpiredDate).toDate();
        values.coa = `${values.productCode}-${values.code}`;
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSelectProduct = (item: IProduct) => {
    form.setFieldsValue({
      productId: item.id,
      productCode: item.code,
      baseUnitName: item.baseUnitName,
      packageUnitName: item.packageUnitName,
      conversion: item.conversion,
      productName: item.name,
      totalBaseQuantity: 1,
      quantity: item.conversion,
    });
  };

  return (
    <Modal
      title={'นำเข้าสต็อก'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      // width={1000}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="stockGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="stock"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
          initialValues={{
            quantity: 1,
          }}
        >
          <Form.Item name={'productId'} hidden />
          <Form.Item name={'productCode'} hidden />
          <Form.Item name={'conversion'} hidden />
          <Row gutter={12}>
            <Col span={24}>
              <div className="flex w-full flex-row items-end gap-3">
                <div className="flex w-full">
                  <Form.Item
                    label="สินค้า"
                    name="productName"
                    rules={[{ required: true, message: 'ระบุสินค้า' }]}
                    className="w-full"
                  >
                    <Input
                      size="large"
                      readOnly
                      placeholder="ค้นหาสินค้าจากด้านขวา"
                    />
                  </Form.Item>
                </div>
                <div className="flex">
                  <Form.Item label="" name="">
                    <ProductSelector
                      handleSelectData={product => {
                        onSelectProduct(product);
                      }}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>
            </Col>

            <Col span={12}>
              <Form.Item<IStock>
                label="จำนวน"
                name={'quantity'}
                rules={[
                  {
                    required: true,
                    message: 'ระบุจำนวน',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const conversion = getFieldValue('conversion');
                      if (!conversion || value % conversion === 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(`จำนวนต้องเต็ม Package (${conversion})xx`),
                      );
                    },
                  }),
                ]}
              >
                <InputNumber
                  disabled={!Form.useWatch('productId', form)}
                  size="large"
                  min={0}
                  step={form.getFieldValue('conversion')}
                  className="w-full"
                  onChange={value => {
                    const conversion = form.getFieldValue('conversion');
                    const totalBaseQuantity = (value || 0) / conversion;
                    form.setFieldsValue({ totalBaseQuantity });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="หน่วยขาย" name="baseUnitName">
                <Input size="large" readOnly />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="จำนวน Package"
                name="totalBaseQuantity"
                rules={[{ required: true, message: 'ระบุจำนวน' }]}
              >
                <InputNumber
                  size="large"
                  min={0}
                  className="w-full"
                  readOnly
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="หน่วย Package" name="packageUnitName">
                <Input size="large" readOnly disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Lot No."
                name={'code'}
                rules={[{ required: true, message: 'ระบุ Lot No.' }]}
              >
                <Input
                  size="large"
                  onChange={e => {
                    const productCode = form.getFieldValue('productCode');
                    const lot = e.target.value;
                    const coa = `${productCode}-${lot}`;
                    form.setFieldsValue({ coa });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IStock>>
                label="วันที่รับเข้า"
                name="stockDate"
                rules={[{ required: true, message: 'เลือกวันที่' }]}
                getValueProps={value => ({
                  value: value && dayjs(Number(value)),
                })}
                normalize={value => value && `${dayjs(value).valueOf()}`}
                initialValue={dayjs()}
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IStock>
                label="IV No."
                name={'ivNo'}
                rules={[{ required: true, message: 'ระบุ IV No.' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IStock> label="PO No." name={'poNo'}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IStock> label="COA No." name={'coa'}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<Partial<IStock>>
                label="วันหมดอายุ"
                name="coaExpiredDate"
                rules={[{ required: true, message: 'เลือกวันที่วันหมดอายุ' }]}
              >
                <DatePicker
                  size="large"
                  className="w-full"
                  locale={locale}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="หมายเหตุ"
                name="remark"
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default StockAddModal;
